import { useQueryClient } from '@tanstack/react-query';
import { Menu } from 'lucide-react';
import { useState } from 'react';
import { Link, Outlet, ScrollRestoration, useNavigate } from 'react-router';

import { api, errorMessage } from '../../lib/api.ts';
import { SESSION_KEY, useSession } from '../../lib/session.ts';
import { useThemeSync } from '../../lib/theme.ts';
import { useToast } from '../../lib/toast.tsx';
import { Button } from '../ui/button.tsx';
import { Dialog } from '../ui/dialog.tsx';
import { ImpersonationBanner } from './impersonation-banner.tsx';
import { OfflineBanner } from './offline-banner.tsx';
import { SidebarNav } from './sidebar.tsx';
import { NotificationBell } from './notification-bell.tsx';
import { UserMenu } from './user-menu.tsx';

/** Layout for signed-in pages: sidebar (desktop), menu sheet (mobile), header. */
export function AppShell() {
  const { data: session } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const notify = useToast();
  useThemeSync(session);

  if (!session) return null;

  const logout = async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch (error) {
      notify(errorMessage(error), 'error');
      return;
    }
    queryClient.clear();
    queryClient.setQueryData(SESSION_KEY, null);
    await navigate('/iniciar-sesion');
  };

  return (
    <div className="min-h-dvh">
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:bg-surface focus:p-3"
      >
        Saltar al contenido
      </a>
      <ImpersonationBanner session={session} />
      <OfflineBanner />
      <div className="flex">
        <aside className="sticky top-0 hidden h-dvh w-72 shrink-0 flex-col overflow-y-auto border-r border-border bg-surface lg:flex">
          <BrandHeader tenant={session.tenant.name} />
          <div className="p-4">
            <SidebarNav session={session} />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-30 flex min-h-14 items-center gap-2 border-b border-border bg-surface/95 px-3 backdrop-blur sm:px-6">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              aria-label="Abrir menú"
              onClick={() => {
                setMenuOpen(true);
              }}
            >
              <Menu />
            </Button>
            <p className="truncate font-semibold lg:hidden">{session.tenant.name}</p>
            <div className="ml-auto flex items-center gap-1">
              <NotificationBell timeZone={session.tenant.timezone} />
              <UserMenu session={session} onLogout={() => void logout()} />
            </div>
          </header>

          <main id="contenido" tabIndex={-1} className="mx-auto w-full max-w-6xl p-4 sm:p-6">
            <Outlet />
          </main>
          {/* A new page starts at the top (back/forward restore the previous position). */}
          <ScrollRestoration />
        </div>
      </div>

      <Dialog
        open={menuOpen}
        onClose={() => {
          setMenuOpen(false);
        }}
        title={session.tenant.name}
        side
        brand
      >
        <SidebarNav
          session={session}
          onNavigate={() => {
            setMenuOpen(false);
          }}
        />
      </Dialog>
    </div>
  );
}

/** Brand moment: the gradient carries the tenant's name (white text on the dark part). */
function BrandHeader({ tenant }: { tenant: string }) {
  return (
    <Link to="/" className="bg-brand block px-6 pt-6 pb-5">
      <span className="block text-xs font-semibold tracking-widest text-white/80 uppercase">
        ERP Inmobiliario
      </span>
      <span className="mt-1 block text-lg leading-tight font-semibold">{tenant}</span>
    </Link>
  );
}
